import { Thresholds } from "@src/types/dashboards";
import store from "@src/reducers/store";
import { formatDateTimeForGraphLabel } from "@src/cloud-connect/utils/format-date-time-for-graph-label";
import { formatChartTooltipDate } from "@src/cloud-connect/utils/timezone-display-utils";
import { buildTooltipDateHeader } from "@src/cloud-connect/charts/chart-tooltip-utils";
import type { ChartThemeColors } from "../use-theme-colors-for-charts";
import { parseManualBounds } from "../axis-bounds";

// Reuse the threshold colours: warning dotted laserOrange, critical dashed
// alarmRed — same shape as the trend-line, so a multi-line chart annotating
// the y-axis reads identical.
export const WARNING_THRESHOLD_COLOR = "#fd7f38"; // laserOrange
export const CRITICAL_THRESHOLD_COLOR = "#fd3838"; // alarmRed

// Per-binding status legend colours. Used when a fetch fails (red, in
// line with `CRITICAL_THRESHOLD_COLOR`) or returns zero points (yellow,
// distinct from threshold-orange so legs / series don't blend together).
// "ok" falls back to the chart-wide text colour below.
export const LEGEND_STATUS_ERROR_COLOR = "#fd3838"; // alarmRed
export const LEGEND_STATUS_EMPTY_COLOR = "#ffc107"; // amber-yellow

// Encoding-roles defaults per multi-line spec §1: cycle in `order` to pick
// the next un-used palette slot when adding a row.
export const MULTI_LINE_DEFAULT_COLORS = [
  "#3aacc8", // value_0 blue accent
  "#027680", // value_1 green
  "#fd7f38", // value_2 laserOrange
  "#fd3838", // value_3 alarmRed
  "#e6f1f2", // value_4 white
] as const;

export interface MultiLineSeries {
  /** Display name shown in legend + tooltip (live = node name; mock = "Series N"). */
  name: string;
  times: string[];
  values: (number | null)[];
  /** Aggregated: per-bucket minimums (parallel to times/values). When
   *  provided AND showMin is true at the option-args level, drawn as a
   *  dashed lighter-opacity sub-series named `"<name> (min)"`. */
  minValues?: (number | null)[];
  /** Aggregated: per-bucket maximums. Mirror of minValues for showMax. */
  maxValues?: (number | null)[];
  /** Override tint for the line + marker + tooltip bullet. Falls back to
   *  MULTI_LINE_DEFAULT_COLORS[index] when omitted. */
  color?: string;
  /** Active-timeseries breadcrumbs to surface under the name in the tooltip. */
  breadcrumbs?: string;
  /**
   * Per-row data health surfaced on the legend chip. Default `ok` (also
   * the case when this field is omitted): "normal" white chip. `error` =
   * the fetch failed — chip goes alarmRed. `empty` = fetch succeeded but
   * zero points came back — chip goes amber-yellow. Only honored when
   * the row's underlying fetch has actually run; mock + cached previews
   * stay neutral.
   */
  status?: "ok" | "empty" | "error";
}

export interface BuildMultiLineOptionArgs {
  series: MultiLineSeries[];
  unitLabel?: string;
  thresholds?: Thresholds | null;
  /** When true AND `series[i].minValues` is set, render per-row v_min
   *  sub-series at lighter opacity of the parent color. Aggregated-only
   *  feature — semantically identical to trend-line's show_min. */
  showMin?: boolean;
  /** Drive semantics.show_max — render per-row v_max sub-series. */
  showMax?: boolean;
  /** Manual Y bounds (engineering units). Applied only when both are
   *  present, finite and ordered; otherwise the automatic range is used. */
  yMin?: number | null;
  yMax?: number | null;
  /** Static/read-only rendering (picker preview or unbound widget): no tooltip, no hover. */
  static?: boolean;
  /** Resolved theme colours from `useThemeColorsForCharts`. Drives
   *  legend, axis labels, and tooltip text. Omitted options fall
   *  back to the pre-picker baseline. */
  themeColors?: ChartThemeColors;
}

/** 1/2/5×10ⁿ "nice" step — same family as trend-line. */
const niceStep = (range: number): number => {
  if (range <= 0 || !Number.isFinite(range)) return 1;
  const pow = 10 ** Math.floor(Math.log10(range));
  const frac = range / pow;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return niceFrac * pow;
};

/**
 * Y-axis range covering ALL series (not just the first) plus any enabled
 * thresholds, with a 10% margin, snapped to a "nice" step. Same style as the
 * single-line variant: an explicit max that doesn't land on the tick grid
 * (e.g. threshold 50 + 10% margin → 51) leaves the top border labelled with
 * a non-grid value while the other ticks stay clean.
 *
 * Aggregated bounds participate in the range calculation too: when the user
 * has flipped show_min / show_max on, the rendered sub-series should fit
 * inside the y-axis. We fold them into the same pool of points since each
 * series keeps its own min/max arrays.
 */
export const calculateMultiLineInterval = (
  seriesValues: Array<(number | null)[]>,
  thresholds?: Thresholds | null,
  extraValues?: Array<(number | null)[] | undefined>,
): { yAxisMin: number; yAxisMax: number } => {
  const nums: number[] = [];
  for (const values of seriesValues) {
    for (const v of values) {
      if (v != null) nums.push(v);
    }
  }
  const thresholdNums = [thresholds?.warning, thresholds?.critical].filter(
    (v): v is number => v != null,
  );
  const extraNums = (extraValues ?? []).flatMap((arr) =>
    (arr ?? []).filter((v): v is number => v != null),
  );
  const all = [...nums, ...thresholdNums, ...extraNums];
  if (all.length === 0) {
    return { yAxisMin: 0, yAxisMax: 1 };
  }
  const minValue = Math.min(...all);
  const maxValue = Math.max(...all);
  const range = maxValue - minValue || 1;
  const margin = 0.1 * range;
  const rawMin = minValue - margin;
  const rawMax = maxValue + margin;
  const step = niceStep((rawMax - rawMin) / 5);
  const eps = 1e-9;
  // Both bounds are multiples of `step`, so rounding to the step's decimals
  // only strips float noise (194 * 0.1 is 19.400000000000002). Echarts rounds
  // its own snapped extent to the tick precision, which lands just below a
  // noisy min (19.4) and trips its dev-only consistency assert.
  const stepDecimals = Math.min(
    100,
    Math.max(0, -Math.floor(Math.log10(step))),
  );
  const snap = (value: number) => Number(value.toFixed(stepDecimals));
  const yAxisMin = snap(Math.floor(rawMin / step + eps) * step);
  let yAxisMax = snap(Math.ceil(rawMax / step - eps) * step);
  if (yAxisMin === yAxisMax) yAxisMax = snap(yAxisMin + step);
  return { yAxisMin, yAxisMax };
};

/**
 * Tooltip formatter: timestamp header + one row per series (marker + name +
 * breadcrumbs + value + unit). Mirrors the trend-line style but for N rows.
 */
const buildTooltipFormatter =
  (
    seriesList: MultiLineSeries[],
    unitLabel: string,
    tooltipTextColor: string,
  ) =>
  (params: any) => {
    const first = Array.isArray(params) ? params[0] : params;
    const { timezone, language } = store.getState().yourSlice;
    const formattedDate = formatChartTooltipDate(
      first.axisValue,
      timezone,
      language,
    );

    // Cap every tooltip slot to 160px so the box can't grow past where
    // echarts' `confine` would still let it overflow the chart canvas.
    let html = `<div style="max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${buildTooltipDateHeader(
      formattedDate,
    )}</div>`;

    // params is an array of one entry per series at the hovered timestamp.
    // We order rows by series index so the legend order matches the order
    // of the bindings (stable). The colour comes from each series' marker
    // (echarts fills `markerColor` / `color` automatically).
    const paramsArr: any[] = Array.isArray(params) ? params : [params];
    seriesList.forEach((s, idx) => {
      // Skip rows whose underlying fetch is unhealthy — the legend chip
      // already signals red/yellow, and the fallback `paramsArr[idx]` below
      // would otherwise hand us a different binding's value at the same
      // index position (echarts emits no param for a series with no points,
      // so `find` returns undefined and the fallback picks the wrong row).
      if (s.status === "empty" || s.status === "error") return;
      // Only the row's own param is trustworthy. Falling back to
      // `paramsArr[idx]` misassigns values: the params array is indexed by
      // ECharts series (which includes the (min)/(max) sub-series) and omits
      // legend-hidden rows, so the index no longer lines up with this row.
      const p = paramsArr.find((x) => x.seriesName === s.name);
      if (!p) return;
      const markerColor =
        p?.color ?? s.color ?? MULTI_LINE_DEFAULT_COLORS[idx] ?? "#027680";
      const rawValue = p?.data;
      const valueText = rawValue != null ? rawValue : "—";
      html += `<div style="display: flex; gap: 5px; align-items: center; color: ${tooltipTextColor}; font-weight: 100; padding-top: ${
        idx === 0 ? 2 : 0
      }px;">
        <span style="color: ${markerColor};">●</span>
        <span style="display: inline-block; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle;">${
          s.name
        }</span>
        <span style="margin-left: auto; font-weight: 800;">${valueText}${
        unitLabel ? ` ${unitLabel}` : ""
      }</span>
      </div>`;
      if (s.breadcrumbs) {
        html += `<div style="padding-left: 15px; color: ${tooltipTextColor}; font-size: 10px; max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${s.breadcrumbs}</div>`;
      }
    });
    return html;
  };

/**
 * ECharts keys the legend (and the legend-driven series filtering) by series
 * `name` and silently drops duplicate names, so two rows whose nodes share a
 * display name would collapse into one legend chip that toggles both lines.
 * Suffixing the repeats gives every row its own legend entry, data, and
 * breadcrumbs.
 */
export const withUniqueNames = <
  T extends {
    name: string;
    minValues?: (number | null)[];
    maxValues?: (number | null)[];
  },
>(
  series: T[],
  showMin: boolean,
  showMax: boolean,
): T[] => {
  const used = new Set<string>();
  // The (min)/(max) sub-series share ECharts' single name namespace with the
  // rows, so reserve them too: a node can itself be named "Pressure (min)".
  const derived = (s: T, name: string) => [
    ...(showMin && s.minValues ? [`${name} (min)`] : []),
    ...(showMax && s.maxValues ? [`${name} (max)`] : []),
  ];
  return series.map((s) => {
    let name = s.name;
    let suffix = 1;
    // Bump until unused — a node can itself be named "Temperature (2)".
    while (used.has(name) || derived(s, name).some((d) => used.has(d))) {
      name = `${s.name} (${++suffix})`;
    }
    used.add(name);
    derived(s, name).forEach((d) => used.add(d));
    return name === s.name ? s : { ...s, name };
  });
};

export const buildMultiLineOption = ({
  series,
  unitLabel = "",
  thresholds,
  showMin = false,
  showMax = false,
  yMin,
  yMax,
  static: isStatic = false,
  themeColors,
}: BuildMultiLineOptionArgs): any => {
  if (series.length === 0) {
    return { series: [] };
  }
  const named = withUniqueNames(series, showMin, showMax);
  const allValues = named.map((s) => s.values);
  // Resolved theme colours — see trend-line `themeColors` doc; same
  // fallback strategy.
  const primaryText = themeColors?.primary ?? "#e6f1f2";
  const secondaryText = themeColors?.secondary ?? "#697477";
  // Aggregated bounds get factored into the y-axis range when their
  // toggle is on and the data is present. We pass them in as separate
  // arrays so the caller decides whether they're displayable.
  const extras: Array<(number | null)[] | undefined> = [];
  if (showMin) extras.push(...named.map((s) => s.minValues));
  if (showMax) extras.push(...named.map((s) => s.maxValues));
  const manual = parseManualBounds(yMin, yMax);
  const { yAxisMin, yAxisMax } = manual
    ? { yAxisMin: manual.min, yAxisMax: manual.max }
    : calculateMultiLineInterval(allValues, thresholds, extras);

  const markLineData = [
    ...(thresholds?.warning != null
      ? [{ yAxis: thresholds.warning, name: "Warning" }]
      : []),
    ...(thresholds?.critical != null
      ? [{ yAxis: thresholds.critical, name: "Critical" }]
      : []),
  ];

  // All series share xAxis.data = series[0].times. V1 assumes aligned
  // timestamps (same from/to/frequency across bindings). Mis-aligned series
  // would just show nulls between their gaps — acceptable for now.
  const xTimes = series[0].times;

  return {
    // Same grid + legend layout as trend-line: top-left series legend,
    // breathing room on right so the last x-axis timestamp isn't clipped.
    grid: { left: 8, right: 28, top: 32, bottom: 8, containLabel: true },
    legend: {
      show: true,
      // Static: legend is display-only — clicking must not hide a series.
      selectedMode: isStatic ? false : true,
      top: 0,
      left: 8,
      itemWidth: 16,
      itemHeight: 12,
      padding: [2, 8, 2, 0],
      textStyle: {
        color: primaryText,
        fontSize: 9,
        overflow: "truncate",
        width: 140,
      },
      // Omit the ` (min)` / ` (max)` sub-series from the legend block.
      // The dashed lines still draw (the series entries are unchanged),
      // but their legend entries don't take up clusters above the chart.
      // Click linkage — when the user clicks a row's avg entry, the
      // matching "(min)" / "(max)" sub-series also toggle — lives in
      // the chart wrapper (multi-line-widget.tsx ResizeAwareChart) via
      // the `legendselectchanged` event handler attached at mount.
      //
      // When a row carries `status` ("error" / "empty"), its legend
      // entry swaps to an object so echarts picks up a per-chip
      // `textStyle.color`. Default ("ok") stays a plain string to keep
      // the ECharts handling cheap.
      data: named.map((s) =>
        s.status === "error"
          ? {
              name: s.name,
              textStyle: { color: LEGEND_STATUS_ERROR_COLOR },
            }
          : s.status === "empty"
          ? {
              name: s.name,
              textStyle: { color: LEGEND_STATUS_EMPTY_COLOR },
            }
          : s.name,
      ),
    },
    xAxis: {
      type: "category",
      data: xTimes,
      boundaryGap: false,
      axisLabel: {
        color: secondaryText,
        formatter: formatDateTimeForGraphLabel,
        fontSize: 9,
        margin: 10,
      },
    },
    yAxis: {
      type: "value",
      min: yAxisMin,
      max: yAxisMax,
      scale: true,
      axisLine: { onZero: true },
      splitLine: { lineStyle: { color: "rgba(255,255,255,0.06)" } },
      axisLabel: {
        color: secondaryText,
        width: 44,
        align: "right",
        overflow: "truncate",
      },
    },
    tooltip: {
      show: !isStatic,
      trigger: "axis",
      triggerOn: !isStatic ? "mousemove|click" : "none", // static: never fires
      confine: true,
      backgroundColor: "rgba(19, 27, 32, 0.5)",
      borderColor: "rgba(230, 241, 242, 0.2)",
      borderWidth: 1,
      textStyle: { color: primaryText, fontFamily: "Inter", fontSize: 11 },
      formatter: buildTooltipFormatter(named, unitLabel, primaryText),
      extraCssText:
        "backdrop-filter: blur(5px); padding: 8px 12px; box-sizing: border-box;",
    },
    series: named.flatMap((s, idx) => {
      const color = s.color ?? MULTI_LINE_DEFAULT_COLORS[idx] ?? "#027680";
      const main = {
        name: s.name,
        type: "line",
        data: s.values,
        smooth: false,
        lineStyle: { color, width: 2 },
        symbol: "circle",
        symbolSize: 6,
        showSymbol: s.values.length === 1,
        itemStyle: { color },
        connectNulls: true,
        animation: true,
        silent: isStatic,
        ...(markLineData.length > 0
          ? {
              markLine: {
                silent: true,
                symbol: "none",
                label: { show: false },
                animation: false,
                data: markLineData.map((m) => ({
                  yAxis: m.yAxis,
                  lineStyle: {
                    color:
                      m.name === "Critical"
                        ? CRITICAL_THRESHOLD_COLOR
                        : WARNING_THRESHOLD_COLOR,
                    type: m.name === "Critical" ? "dashed" : "dotted",
                    width: 1.5,
                  },
                })),
              },
            }
          : {}),
      };
      // Aggregated bounds sub-series. flatMap keeps the main avg entry
      // first so echarts paints it under the bounds. Lighter dashed
      // style so the avg line stays the visually dominant one.
      const sub: any[] = [];
      if (showMin && s.minValues) {
        sub.push({
          name: `${s.name} (min)`,
          type: "line",
          data: s.minValues,
          smooth: false,
          lineStyle: { color, width: 1.5, type: "dashed", opacity: 0.6 },
          symbol: "none",
          itemStyle: { color, opacity: 0.6 },
          connectNulls: true,
          animation: true,
          silent: isStatic,
        });
      }
      if (showMax && s.maxValues) {
        sub.push({
          name: `${s.name} (max)`,
          type: "line",
          data: s.maxValues,
          smooth: false,
          lineStyle: { color, width: 1.5, type: "dashed", opacity: 0.6 },
          symbol: "none",
          itemStyle: { color, opacity: 0.6 },
          connectNulls: true,
          animation: true,
          silent: isStatic,
        });
      }
      return [main, ...sub];
    }),
  };
};
